import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * A dialog that helps shoppers work out their ring size, offering two
 * measurement methods (finger or existing ring) plus a link to a full guide
 * page.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} chooseStep
 * @property {HTMLElement} fingerStep
 * @property {HTMLElement} ringStep
 *
 * @extends DialogComponent
 */
export class RingSizeFinderDialog extends DialogComponent {
  requiredRefs = ['dialog', 'chooseStep', 'fingerStep', 'ringStep'];

  connectedCallback() {
    super.connectedCallback();
    // Always land back on the method-choice step whenever the dialog is
    // reopened, regardless of which step it was left on last time.
    this.addEventListener(DialogCloseEvent.eventName, this.showChoose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.showChoose);
  }

  /**
   * Shows the instructions for the chosen measurement method.
   * @param {Event} event
   */
  showMethod = (event) => {
    // Note: with this theme's declarative `on:click` event system, the
    // handler is invoked from a single delegated document-level listener,
    // so `event.currentTarget` is always `document`. The framework instead
    // proxies `event.target` to resolve to the actual element that carries
    // the `on:click` attribute - that's what must be used here.
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const { method } = button.dataset;
    const { chooseStep, fingerStep, ringStep } = this.refs;

    chooseStep.hidden = true;
    fingerStep.hidden = method !== 'finger';
    ringStep.hidden = method !== 'ring';

    // Move focus into the newly revealed step for keyboard/screen reader users.
    const revealed = method === 'finger' ? fingerStep : ringStep;
    const heading = revealed.querySelector('h3');
    if (heading instanceof HTMLElement) {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    }
  };

  /**
   * Returns to the initial "choose a method" step.
   */
  showChoose = () => {
    const { chooseStep, fingerStep, ringStep } = this.refs;
    chooseStep.hidden = false;
    fingerStep.hidden = true;
    ringStep.hidden = true;
  };
}

if (!customElements.get('ring-size-finder-dialog')) {
  customElements.define('ring-size-finder-dialog', RingSizeFinderDialog);
}
